import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useB2BActivity } from '@app/pages/b2b/hooks';
import {
  queryClient,
  QuestionOption,
  showDefaultErrorToast,
  useHandleFormErrors,
  useQuestionsList,
  useToggleState,
} from '@app/utils';
import { questionTranslationsKeys, trialCriteriaKeys } from '@data/constants';
import {
  useCreateQuestion,
  useCreateQuestionTranslations,
  useEditCriteria as useEditCriteriaMutation,
} from '@data/hooks';
import {
  AnswerType,
  CriteriaUpdateBody,
  QuestionCriterionView,
  TranslationBody,
  TrialCriteriaView,
} from '@data/sdk/match';
import { Language, Namespace } from '@data/utils/enums';
import { AntdFormInstance, antdNotification } from '@ui/components';
import { EditCriteriaFormValues, ModifyMode } from './useFormData';

type UseEditCriteriaProps = {
  criteria:
    | TrialCriteriaView
    | (QuestionCriterionView & {
        answerType: AnswerType;
        questionId: string;
        questionText: string;
      });
  form: AntdFormInstance<EditCriteriaFormValues>;
  initialValues: EditCriteriaFormValues;
  invalidateQuestionCriteria?: () => void;
  isQuestionCriteria: boolean;
  onClose: () => void;
  trialId: string;
};

const TRANSLATIONS_REFETCH_DELAY_MS = 2000;

const getTranslationsBody = (
  enText: string,
  isAutoTranslateEnabled: boolean,
): TranslationBody[] => {
  if (isAutoTranslateEnabled) {
    return Object.values(Language).map((language) => {
      const shouldAutoGenerate = language !== Language.EN;
      return {
        language,
        value: enText,
        autoGenerate: shouldAutoGenerate,
      };
    });
  }
  return [{ language: Language.EN, value: enText, autoGenerate: false }];
};

export const useEditCriteria = ({
  initialValues,
  form,
  onClose,
  isQuestionCriteria,
  trialId,
  criteria,
  invalidateQuestionCriteria,
}: UseEditCriteriaProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { trackCriteriaEdit } = useB2BActivity();

  const [createCustomQuestion, setCreateCustomQuestion] = useState(false);
  const [showSuggestedQuestions, toggleShowSuggestedQuestions] = useToggleState(true);

  const { mutate: editCriteria } = useEditCriteriaMutation();
  const { mutateAsync: createQuestion } = useCreateQuestion();
  const { mutateAsync: createQuestionTranslations } = useCreateQuestionTranslations();

  const { questionOptions, questionsLoading, searchText, setSearchText, selectOptions } =
    useQuestionsList([AnswerType.Boolean, AnswerType.Number]);

  const showSuggestedList =
    createCustomQuestion && showSuggestedQuestions && !!questionOptions.length;

  const handleModeChange = (mode: ModifyMode) => {
    if (mode === ModifyMode.CreateNew) {
      form.setFieldsValue({
        question: undefined,
        questionText: undefined,
        answerType: AnswerType.Boolean,
        value: null,
        minThreshold: null,
        maxThreshold: null,
        unit: undefined,
      });
      setCreateCustomQuestion(false);
    } else if (mode === ModifyMode.ModifyExisting) {
      form.setFieldsValue({
        question: initialValues.question,
        questionText: initialValues.questionText,
        answerType: initialValues.answerType,
        value: initialValues.value,
        minThreshold: initialValues.minThreshold,
        maxThreshold: initialValues.maxThreshold,
        unit: initialValues.unit,
        autoTranslate: true,
      });
    }
  };

  const handleAnswerTypeChange = () => {
    form.setFieldsValue({
      value: null,
      minThreshold: null,
      maxThreshold: null,
      unit: null,
    });
  };

  const handleClose = () => {
    form.resetFields();
    setSearchText('');
    setCreateCustomQuestion(false);
    form.setFieldValue('mode', ModifyMode.ModifyExisting);
    onClose();
  };

  const handleClearAnswerFields = () => {
    form.setFieldsValue({
      value: null,
      minThreshold: null,
      maxThreshold: null,
      unit: null,
      answerType: undefined,
    });
  };

  const handleReplaceQuestion = (value: QuestionOption) => {
    setCreateCustomQuestion(false);
    form.setFieldValue('question', value);
    form.setFieldValue('answerType', value.answerType);
    form.setFieldValue('unit', value.unit);
  };

  const handleSelectQuestion = (value: QuestionOption) => {
    handleClearAnswerFields();
    form.setFieldValue('question', value);
    setCreateCustomQuestion(!!value.createOwn);
    if (!value.createOwn) {
      form.setFieldValue('answerType', value.answerType);
      form.setFieldValue('unit', value.unit);
    }
    form.setFieldValue('mode', ModifyMode.CreateNew);
  };

  const handleClearSelect = () => {
    setSearchText('');
    handleClearAnswerFields();
    setCreateCustomQuestion(false);
  };

  const invalidateQueryWithDelay = (queryKey: readonly unknown[]) => {
    queryClient.invalidateQueries({ queryKey });
    setTimeout(() => {
      queryClient.invalidateQueries({ queryKey });
    }, TRANSLATIONS_REFETCH_DELAY_MS);
  };

  const handleEditCriteria = (values: CriteriaUpdateBody) => {
    editCriteria(
      {
        criteriaId: isQuestionCriteria
          ? (criteria as QuestionCriterionView).criteriaId
          : criteria.id,
        values,
      },
      {
        onSuccess: () => {
          antdNotification.success({ title: t('trial_page.criteria_successfully_edited') });
          handleClose();
          if (isQuestionCriteria && invalidateQuestionCriteria) {
            invalidateQuestionCriteria();
          }

          const changedFields = [
            ...(initialValues.originalCriteriaText !== values.originalCriteriaText
              ? ['originalCriteriaText']
              : []),
            ...(initialValues.question.value !== values.questionId ? ['question'] : []),
            ...(initialValues.value !== values.value && typeof values.value === 'boolean'
              ? ['value']
              : []),
            ...(initialValues.minThreshold !== values.minThreshold &&
            (typeof values.minThreshold === 'number' ||
              (typeof initialValues.minThreshold === 'number' && !values.minThreshold))
              ? ['minThreshold']
              : []),
            ...(initialValues.maxThreshold !== values.maxThreshold &&
            (typeof values.maxThreshold === 'number' ||
              (typeof initialValues.maxThreshold === 'number' && !values.maxThreshold))
              ? ['maxThreshold']
              : []),
            ...(initialValues.otherValues !== values.otherValues && !!values.otherValues?.length
              ? ['otherValues']
              : []),
          ];
          trackCriteriaEdit(trialId, criteria.id, changedFields);
        },
        onError: (err) => useHandleFormErrors(err, form),
      },
    );
  };

  const handleSubmit = async ({
    mode,
    autoTranslate,
    questionText,
    answerType,
    value,
    question,
    maxThreshold,
    minThreshold,
    otherValues,
    originalCriteriaText,
    unit,
  }: EditCriteriaFormValues) => {
    if (mode === ModifyMode.ModifyExisting) {
      const criteriaChanged =
        initialValues.originalCriteriaText !== originalCriteriaText ||
        (answerType === AnswerType.Boolean && value !== initialValues.value) ||
        (answerType === AnswerType.Number &&
          (maxThreshold !== initialValues.maxThreshold ||
            minThreshold !== initialValues.minThreshold)) ||
        (otherValues !== initialValues.otherValues && !!otherValues?.length);

      // If question text is changed, update translations
      if (
        questionText &&
        questionText !== initialValues.questionText &&
        initialValues.question.value
      ) {
        await createQuestionTranslations(
          {
            id: initialValues.question.value,
            values: {
              translations: getTranslationsBody(questionText, !!autoTranslate),
            },
          },
          {
            onError: showDefaultErrorToast,
            onSuccess: () => {
              if (!criteriaChanged) {
                antdNotification.success({
                  title: t('trial_page.criteria_question_successfully_edited'),
                });
              }
              if (isQuestionCriteria && invalidateQuestionCriteria) {
                invalidateQuestionCriteria();
              }
              invalidateQueryWithDelay(
                questionTranslationsKeys.list(initialValues.question.value || ''),
              );
              invalidateQueryWithDelay(trialCriteriaKeys.all());
            },
          },
        );
      }

      if (criteriaChanged) {
        editCriteria(
          {
            criteriaId: isQuestionCriteria
              ? (criteria as QuestionCriterionView).criteriaId
              : criteria.id,
            values: {
              originalCriteriaText,
              value: answerType === AnswerType.Boolean ? value : undefined,
              maxThreshold: answerType === AnswerType.Number ? Number(maxThreshold) : undefined,
              minThreshold: answerType === AnswerType.Number ? Number(minThreshold) : undefined,
              otherValues: otherValues || undefined,
            },
          },
          {
            onError: showDefaultErrorToast,
            onSuccess: () => {
              handleClose();
              antdNotification.success({ title: t('trial_page.criteria_successfully_edited') });
              if (isQuestionCriteria && invalidateQuestionCriteria) {
                invalidateQuestionCriteria();
              }
              queryClient.invalidateQueries({
                queryKey: trialCriteriaKeys.all(),
              });
            },
          },
        );
      } else {
        handleClose();
      }
    } else if (mode === ModifyMode.CreateNew) {
      // Create new question
      let questionId = question.value || '';

      if (question.createOwn) {
        await createQuestion(
          {
            answerType,
            autoGenerateTranslations: true,
            text: question.label || '',
            ...(unit ? { unit } : {}),
          },
          {
            onSuccess: ({ id }) => {
              questionId = id;
            },
            onError: (err) => {
              if (err.response?.data.message === 'AlreadyExists') {
                antdNotification.error({ title: t('patient:beErrors.QuestionAlreadyExists') });
              } else showDefaultErrorToast(err);
            },
          },
        );
      }

      handleEditCriteria({
        questionId,
        originalCriteriaText,
        value: answerType === AnswerType.Boolean ? value : undefined,
        maxThreshold: answerType === AnswerType.Number ? Number(maxThreshold) : undefined,
        minThreshold: answerType === AnswerType.Number ? Number(minThreshold) : undefined,
        otherValues: otherValues || undefined,
      });
    }
  };

  return {
    questionOptions,
    questionsLoading,
    searchText,
    selectOptions,
    showSuggestedList,
    createCustomQuestion,
    setSearchText,
    handleClose,
    handleSubmit,
    handleModeChange,
    handleClearSelect,
    handleSelectQuestion,
    handleReplaceQuestion,
    handleAnswerTypeChange,
    toggleShowSuggestedQuestions,
  };
};
